var ResourcesService_1;
import { __decorate } from "tslib";
import { Inject, Injectable, Optional } from '@angular/core';
import { getTimezone } from 'countries-and-timezones';
import { map, of } from 'rxjs';
import { ResourceKeys } from '../model';
import { ConstructorName } from '../decorators';
import * as i0 from "@angular/core";
import * as i1 from "@ngx-translate/core";
import * as i2 from "./api.service";
import * as i3 from "./monitoring.service";
let ResourcesService = class ResourcesService {
  static {
    ResourcesService_1 = this;
  }
  portalPath;
  translate;
  api;
  monitoringService;
  url = 'cultures/v1';
  cache$ = null;
  constructor(portalPath, translate, api, monitoringService) {
    this.portalPath = portalPath;
    this.translate = translate;
    this.api = api;
    this.monitoringService = monitoringService;
    this.monitoringService.sendTrackEvent(Reflect.getMetadata('constructorName', ResourcesService_1), 'constructor');
    this.setUrl(this.url);
  }
  static getUtcOffset(zoneKey) {
    const timezone = getTimezone(zoneKey);
    return timezone ? timezone.utcOffset : 0;
  }
  static getTimeshiftValue(zoneKey) {
    const timezone = getTimezone(zoneKey);
    return timezone ? `(UTC${timezone.utcOffsetStr}) ${zoneKey}` : zoneKey;
  }
  // Helper for translating countries from country codes
  translateCountry(country) {
    const key = 'UI-LIB.COUNTRIES.' + country;
    const temp = this.translate.instant(key);
    if (temp !== key) return country + ' - ' + temp;else return country;
  }
  getResources(forceReload = false, ...keys) {
    const resourcesToFetch = this.checkForResourcesToFetch(forceReload, keys);
    if (forceReload || !this.cache$ || this.cache$ && resourcesToFetch.length > 0) {
      return this.api.get(this.url + this.makeResourceUrlKeys(resourcesToFetch), {}).pipe(map(response => {
        this.cache$ = {
          ...this.cache$,
          ...response.resources
        };
        return response.resources;
      }));
    }
    return of(this.cache$);
  }
  checkForResourcesToFetch(forceReload, keys) {
    const uniqueKeyArr = Array.from(new Set(keys));
    if (forceReload || !this.cache$) {
      return uniqueKeyArr;
    }
    return uniqueKeyArr.filter(key => this.cache$ && !this.cache$[key]);
  }
  makeResourceUrlKeys(keys) {
    let resourceUrl = '';
    keys.forEach((key, index) => {
      if (index === 0) {
        resourceUrl += `?keys=${key}`;
      } else {
        resourceUrl += `&keys=${key}`;
      }
    });
    return resourceUrl;
  }
  fetchMultipleResourcesAndGetOptionsModel(...keys) {
    const uniqueKeyArr = Array.from(new Set(keys));
    return this.getResources(false, ...uniqueKeyArr).pipe(map(resources => this.makeResourceSelectOptionsModel({
      ...this.cache$,
      ...resources
    })));
  }
  makeResourceSelectOptionsModel(resources) {
    const resourcesSelOptModel = {};
    if (resources[ResourceKeys.LOCALES]) {
      resourcesSelOptModel.languageOptions = this.mapLanguagesToSelectOption(resources);
    }
    if (resources[ResourceKeys.TIMEZONES]) {
      resourcesSelOptModel.timeZoneOptions = this.mapTimeZonesToSelectOptions(resources);
    }
    if (resources[ResourceKeys.TITLES]) {
      resourcesSelOptModel.titleOptionsAll = resources.titles;
    }
    if (resources[ResourceKeys.COUNTRIES]) {
      resourcesSelOptModel.countryOptions = this.mapCountiesToSelectOption(resources);
    }
    if (resources[ResourceKeys.ORGATYPES]) {
      resourcesSelOptModel.orgaTypeOptions = this.mapOrgaTypesToOption(resources);
    }
    return resourcesSelOptModel;
  }
  get languages$() {
    return this.getResources(false, ResourceKeys.LOCALES).pipe(map(resources => resources.locales));
  }
  getLanguageOptions() {
    return this.getResources(false, ResourceKeys.LOCALES).pipe(map(resources => this.mapLanguagesToSelectOption(resources)));
  }
  mapLanguagesToSelectOption(resources) {
    return resources.locales.map(locale => {
      return {
        name: locale.toUpperCase(),
        value: locale
      };
    });
  }
  getTimeZoneOptions() {
    return this.getResources(false, ResourceKeys.TIMEZONES).pipe(map(resources => this.mapTimeZonesToSelectOptions(resources)));
  }
  mapTimeZonesToSelectOptions(resources) {
    return resources.timezones.map(zone => {
      return {
        value: zone,
        utcOffset: ResourcesService_1.getUtcOffset(zone)
      };
    }).sort((a, b) => a.utcOffset - b.utcOffset).map(zone => {
      return {
        name: ResourcesService_1.getTimeshiftValue(zone.value),
        value: zone.value
      };
    });
  }
  getTitles() {
    return this.getResources(false, ResourceKeys.TITLES).pipe(map(resources => resources.titles));
  }
  getCountries() {
    return this.getResources(false, ResourceKeys.COUNTRIES).pipe(map(resources => this.mapCountiesToSelectOption(resources)));
  }
  mapCountiesToSelectOption(resources) {
    return resources.countries?.map(country => ({
      name: this.translateCountry(country),
      value: country
    }));
  }
  getOrgaTypes() {
    return this.getResources(false, ResourceKeys.ORGATYPES).pipe(map(resources => {
      return this.mapOrgaTypesToOption(resources);
    }));
  }
  getOrgaTypesV2Admin() {
    return this.getResources(false, ResourceKeys.ORGATYPES).pipe(map(res => res.organizationTypes ?? []));
  }
  mapOrgaTypesToOption(resources) {
    return resources.organizationTypes?.map(orgaType => {
      return {
        value: orgaType.type,
        nameKey: `X.ORGA_TYPE.${orgaType.type}`
      };
    });
  }
  setUrl(url) {
    if (url) {
      this.url = url;
    }
    return this;
  }
  static ɵfac = function ResourcesService_Factory(t) {
    return new (t || ResourcesService)(i0.ɵɵinject('portalPath', 8), i0.ɵɵinject(i1.TranslateService), i0.ɵɵinject(i2.ApiService), i0.ɵɵinject(i3.MonitoringService));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ResourcesService,
    factory: ResourcesService.ɵfac,
    providedIn: 'root'
  });
};
ResourcesService = ResourcesService_1 = __decorate([ConstructorName('ResourcesService')], ResourcesService);
export { ResourcesService };
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ResourcesService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: ['portalPath']
    }]
  }, {
    type: i1.TranslateService
  }, {
    type: i2.ApiService
  }, {
    type: i3.MonitoringService
  }], null);
})();
